import { getFormatter, isDefined } from '@eih-netbroker/common';
import { useCallback, useState } from 'react';
import { ReactComponent as ArrowRightIcon } from '../../../../assets/icons/arrow-right-line.svg';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { ModalFooter } from '../../../../shared/components/modal/ModalFooter';
import { ModalHeader } from '../../../../shared/components/modal/ModalHeader';
import { usePayment } from '../../services/usePayment';
import classes from './CashActionSummary.module.scss';

export type CashActionSummaryProps = {
  title: string;
  submitButtonText: string;
  amountLabel: string;
  sourceAccountText?: string;
  targetAccountText?: string;
  onClose: () => void;
  onBack: () => void;
  onSubmit: () => Promise<void>;
};

export const CashActionSummary = ({
  title,
  amountLabel,
  submitButtonText,
  sourceAccountText,
  targetAccountText,
  onClose,
  onBack,
  onSubmit,
}: CashActionSummaryProps) => {
  const { translation } = useLocale();
  const { formatNumberRoundOrF2 } = getFormatter();
  const { cashActionTemp } = usePayment();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(async () => {
    setIsLoading(true);
    await onSubmit();
  }, [onSubmit]);

  if (!isDefined(cashActionTemp)) {
    return null;
  }

  return (
    <div className={classes.container}>
      <ModalHeader
        title={title}
        onClose={onClose}
        hideCloseButton={isLoading}
        isSmall
        isWithDivider
        className={classes.header}
      />

      <div className={classes.summaryHeader}>
        <p>{translation.cashAction.summary.header}</p>
      </div>

      <div className={classes.accountRow}>
        <div className={classes.columnSection}>
          <div className={classes.accountCard}>
            <p className={classes.accountLabel}>{translation.cashAction.summary.sourceAccount}</p>
            <p className={classes.accountValue}>{sourceAccountText ?? '-'}</p>
          </div>
          <p className={classes.amountLabel}>{amountLabel}</p>
        </div>

        <ArrowRightIcon className={classes.arrowIcon} />

        <div className={classes.columnSection}>
          <div className={classes.accountCard}>
            <p className={classes.accountLabel}>{translation.cashAction.summary.targetAccount}</p>
            <p className={classes.accountValue}>{targetAccountText ?? '-'}</p>
          </div>

          <div className={classes.amountValue}>
            <span className={classes.amountNumber}>{formatNumberRoundOrF2(cashActionTemp.amount)}</span>
            <span className={classes.amountCurrency}>{cashActionTemp.currencyCode}</span>
          </div>
        </div>
      </div>

      <div className={classes.divider} />

      <ModalFooter
        left={{
          title: translation.general.back,
          action: onBack,
          disabled: isLoading,
        }}
        right={{
          title: submitButtonText,
          action: handleSubmit,
          isLoading,
        }}
      />
    </div>
  );
};
